<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import ClinicalCrmAPI from 'dashboard/api/clinicalCrm';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const emit = defineEmits(['close', 'select']);
const { t } = useI18n();
const assets = ref([]);
const query = ref('');
const isLoading = ref(false);

const filteredAssets = computed(() => {
  const needle = query.value.trim().toLocaleLowerCase();
  if (!needle) return assets.value;
  return assets.value.filter(asset =>
    [asset.title, asset.description, asset.category, asset.fileName]
      .filter(Boolean)
      .some(value => value.toLocaleLowerCase().includes(needle))
  );
});

const fetchAssets = async () => {
  isLoading.value = true;
  try {
    const { data } = await ClinicalCrmAPI.getMediaAssets();
    assets.value = data;
  } finally {
    isLoading.value = false;
  }
};

const iconFor = kind =>
  ({
    image: 'i-lucide-image',
    video: 'i-lucide-video',
    document: 'i-lucide-file-text',
  })[kind] || 'i-lucide-paperclip';

onMounted(fetchAssets);
</script>

<template>
  <woot-modal show :on-close="() => emit('close')">
    <div class="flex flex-col max-h-[80vh] p-6">
      <div class="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 class="text-lg font-semibold text-n-slate-12">
            {{ t('CLINICAL_CRM.MEDIA_LIBRARY.PICKER.TITLE') }}
          </h2>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ t('CLINICAL_CRM.MEDIA_LIBRARY.PICKER.DESCRIPTION') }}
          </p>
        </div>
        <Button icon="i-lucide-x" slate @click="emit('close')" />
      </div>

      <Input
        v-model="query"
        type="search"
        :placeholder="t('CLINICAL_CRM.MEDIA_LIBRARY.PICKER.SEARCH')"
        class="mb-4"
      />

      <div
        v-if="isLoading"
        class="grid min-h-48 place-items-center text-sm text-n-slate-11"
      >
        {{ t('CLINICAL_CRM.MEDIA_LIBRARY.LOADING') }}
      </div>
      <div
        v-else-if="!filteredAssets.length"
        class="grid min-h-48 place-items-center text-sm text-n-slate-11"
      >
        {{ t('CLINICAL_CRM.MEDIA_LIBRARY.PICKER.EMPTY') }}
      </div>
      <div v-else class="grid gap-3 overflow-auto sm:grid-cols-2">
        <article
          v-for="asset in filteredAssets"
          :key="asset.id"
          class="flex gap-3 p-3 border rounded-xl border-n-weak bg-n-alpha-1"
        >
          <img
            v-if="asset.kind === 'image'"
            :src="asset.fileUrl"
            :alt="asset.title"
            class="object-cover w-16 h-16 rounded-lg bg-n-slate-3"
          />
          <div
            v-else
            class="grid flex-shrink-0 w-16 h-16 rounded-lg place-items-center bg-n-slate-3 text-n-slate-11"
          >
            <span class="size-6" :class="[iconFor(asset.kind)]" />
          </div>
          <div class="min-w-0 flex-1">
            <h3 class="truncate text-sm font-medium text-n-slate-12">
              {{ asset.title }}
            </h3>
            <p
              v-if="asset.description"
              class="mt-0.5 text-xs text-n-slate-11 line-clamp-2"
            >
              {{ asset.description }}
            </p>
            <Button
              :label="t('CLINICAL_CRM.MEDIA_LIBRARY.PICKER.ADD')"
              size="sm"
              class="mt-2"
              @click="emit('select', asset)"
            />
          </div>
        </article>
      </div>
    </div>
  </woot-modal>
</template>
